import { EventSentToMobileApp } from "../constants/GlobalTypesAndEnumsAndConstants";
import type { LanguagePreference } from "./localeRegistry";

interface NativeDeviceLanguages {
  version: 1;
  languages: readonly string[];
  platform: "ios" | "android";
}
export interface NativeInterfaceLanguageContext extends NativeDeviceLanguages {
  session: string;
  sequence: number;
  accountId: string | null;
}
declare global {
  interface Window { __JUNGLE_NATIVE_INTERFACE_LANGUAGES__?: unknown }
}

const listeners = new Set<() => void>();
let context: NativeInterfaceLanguageContext | null = null;
let nextGuestSequence = 0;

function parseDeviceLanguages(value: unknown): NativeDeviceLanguages | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  const platform = row.platform;
  const languages = row.languages;
  const hasSupportedVersion = row.version === 1;
  const hasSupportedPlatform = platform === "ios" || platform === "android";
  const hasBoundedLanguageList = Array.isArray(languages) && languages.length >= 1 && languages.length <= 20;
  const hasValidLanguageTags = hasBoundedLanguageList && languages.every(tag =>
    typeof tag === "string" && tag.length <= 80 && /^[A-Za-z]{2,8}(?:[-_][A-Za-z0-9]{1,8})*$/.test(tag));
  // Accept only this protocol's native platforms and a bounded list of OS tags.
  if (!hasSupportedVersion || !hasSupportedPlatform || !hasBoundedLanguageList || !hasValidLanguageTags) return null;
  return Object.freeze({ version: 1, platform, languages: Object.freeze([...languages]) });
}

export function parseNativeInterfaceLanguageContext(raw: unknown): NativeInterfaceLanguageContext | null {
  if (typeof raw !== "string" || raw.length > 4096) return null;
  try {
    const row = JSON.parse(raw);
    const device = parseDeviceLanguages(row);
    if (!device || typeof row.session !== "string" || !/^[A-Za-z0-9-]{1,80}$/.test(row.session) ||
      !Number.isSafeInteger(row.sequence) || row.sequence < 1 || !(row.accountId === null ||
        (typeof row.accountId === "string" && row.accountId.length > 0 && row.accountId.length <= 128))) return null;
    return Object.freeze({ ...device, session: row.session, sequence: row.sequence, accountId: row.accountId });
  } catch { return null; }
}

function hasNativeLanguageBridge(): boolean {
  return typeof window !== "undefined" && typeof window.ReactNativeWebView?.postMessage === "function" &&
    window.__JUNGLE_NATIVE_APP_BUILD_INFO__?.interfaceLanguageBridgeVersion === 1;
}

/** Before-content OS data is a bootstrap hint; foreground events replace it. */
export function nativeInterfaceLanguages(): readonly string[] | null {
  if (!hasNativeLanguageBridge()) return null;
  return (context ?? parseDeviceLanguages(window.__JUNGLE_NATIVE_INTERFACE_LANGUAGES__))?.languages ?? null;
}
export function interfaceLanguageClientSource(): "web" | "ios" | "android" {
  if (!hasNativeLanguageBridge()) return "web";
  return (context ?? parseDeviceLanguages(window.__JUNGLE_NATIVE_INTERFACE_LANGUAGES__))?.platform ?? "web";
}
export const getNativeInterfaceLanguageContext = () => context;
export function subscribeNativeInterfaceLanguageContext(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function receiveNativeInterfaceLanguageContext(raw: unknown): boolean {
  if (!hasNativeLanguageBridge()) return false;
  const next = parseNativeInterfaceLanguageContext(raw);
  if (!next || (context && (next.sequence <= context.sequence ||
    (next.session === context.session && next.accountId !== context.accountId)))) return false;
  if (next.session !== context?.session) nextGuestSequence = 0;
  context = next;
  listeners.forEach(listener => listener());
  return true;
}

export function postNativeGuestInterfacePreference(current: NativeInterfaceLanguageContext, preference: LanguagePreference): boolean {
  if (!hasNativeLanguageBridge() || current !== context || current.accountId !== null) return false;
  // Throwing postMessage is handled by the connector; a failed send is never
  // deduplicated. No account preference is persisted through this protocol.
  window.ReactNativeWebView.postMessage(JSON.stringify({
    typeEvent: EventSentToMobileApp.GUEST_INTERFACE_LANGUAGE,
    message: JSON.stringify({ version: 1, session: current.session, sequence: ++nextGuestSequence, accountId: null, preference }),
  }));
  return true;
}

export function resetNativeInterfaceLanguageContextForTests() {
  context = null;
  nextGuestSequence = 0;
}
